import { trpcClient } from '@maxus/website/lib'

export type PendingFrontendTaskRetry = {
  expectedAttempt: number
  taskId: string
  taskType: 'render' | 'upload'
  workflowId: string
}

type FrontendTaskRetryOperation = PendingFrontendTaskRetry & {
  request?: Promise<FrontendTaskRetryResult>
}

type FrontendTaskRetryResult = Awaited<ReturnType<typeof trpcClient.project.workflow.retryTask.mutate>>

const operations = new Map<string, FrontendTaskRetryOperation>()

const isSameOperation = (operation: FrontendTaskRetryOperation, input: PendingFrontendTaskRetry) =>
  operation.expectedAttempt === input.expectedAttempt &&
  operation.taskId === input.taskId &&
  operation.taskType === input.taskType

export const getPendingFrontendTaskRetry = (
  workflowId: string,
  taskType: PendingFrontendTaskRetry['taskType'],
): PendingFrontendTaskRetry | undefined => {
  const operation = operations.get(workflowId)
  if (!operation || operation.taskType !== taskType) return
  return {
    expectedAttempt: operation.expectedAttempt,
    taskId: operation.taskId,
    taskType: operation.taskType,
    workflowId: operation.workflowId,
  }
}

export const executeFrontendTaskRetry = (input: PendingFrontendTaskRetry): Promise<FrontendTaskRetryResult> => {
  const existing = operations.get(input.workflowId)
  const operation = existing && isSameOperation(existing, input) ? existing : { ...input }
  operations.set(input.workflowId, operation)

  if (operation.request) return operation.request

  const request = trpcClient.project.workflow.retryTask.mutate({
    workflowId: operation.workflowId,
    taskId: operation.taskId,
    expectedAttempt: operation.expectedAttempt,
  })
  operation.request = request

  void request.then(
    () => {
      if (operations.get(input.workflowId) === operation) operations.delete(input.workflowId)
    },
    () => {
      // 保留原 task/attempt；Server 会为同一 attempt 生成相同的 Temporal Update ID。
      if (operations.get(input.workflowId) === operation) operation.request = undefined
    },
  )
  return request
}
